import { DecimalPipe } from "@angular/common";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { IPill } from "@app/directives/_veri/v-pills/v-pills.component";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { IKeyValuePair } from "@app/interfaces/IKeyValuePair";
import { EnumService } from "@app/services/enum.service";
import { TranslationService } from "@services/translation.service";
import { copy } from "angular";
import { lastValueFrom } from "rxjs";
import { IGroup } from "../groups/_models/IGroup";
import { DrivingStyleMetric, DrivingStyleMetricCategory, DrivingStyleMetricDenominator, DrivingStyleType, DrivingStyleWeightingType, MetricMappingWeightingLevel } from "./driving-styles.enums";
import { DrivingStyleMetricDenominatorMap, IDrivingPerformanceSummary, IDrivingStyle, IDrivingStyleAssociation, IDrivingStyleMetricDescriptionDetails, IDrivingStyleMetricMapping, IJourneysWithAssociatedDrivingStyle } from "./driving-styles.interfaces";

export interface IMetricMappingWeightingLevelDetail {
    level: MetricMappingWeightingLevel;
    value: number;
    icon?: string;
    isDefault?: boolean;
    hideFromDropdown?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class DrivingStylesService {

    VehicleType = VehicleType;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly translationService: TranslationService
    ) { }

    getDrivingStyles(invalidateCache: boolean = false, includeDefaultDrivingStyle: boolean = false): Promise<IDrivingStyle[]> {

        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<IDrivingStyle[]>(`/api/drivingstyles/styles/${includeDefaultDrivingStyle}`, options));
    }

    getDefaultDrivingStyles(invalidateCache: boolean = false): Promise<IDrivingStyle[]> {
        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<IDrivingStyle[]>(`/api/drivingstyles/defaultstyles`, options));
    }

    getDrivingStyleForVehicle(vehicleId: number, invalidateCache: boolean = false): Promise<IDrivingStyle> {

        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;
        options.params["vehicleId"] = vehicleId;

        return lastValueFrom(this.httpClient.get<IDrivingStyle>(`/api/drivingstyles/styleforvehicle`, options));
    }

    saveDrivingStyle(drivingStyle: IDrivingStyle, invalidateCache: boolean = false): Promise<number> {

        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.post<number>(`/api/drivingstyles/style`, drivingStyle, options));
    }

    deleteDrivingStyleById(drivingStyleId: number, invalidateCache: boolean = false): Promise<IDrivingStyle> {

        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.delete<IDrivingStyle>(`/api/drivingstyles/style/${drivingStyleId}`, options));
    }

    async getDrivingStyleMetricDescriptionDetails(invalidateCache: boolean = false): Promise<IDrivingStyleMetricDescriptionDetails[]> {

        const metricDenominatorMap = await this.getDrivingStyleMetricsWithDenominations(invalidateCache);

        if (!metricDenominatorMap) {
            console.warn("Couldn't get metrics denominators");
            return;
        }

        const denominatorKeys = Object.keys(metricDenominatorMap);

        if (!denominatorKeys?.length) {
            console.warn("No metrics denominators in map");
            return;
        }

        const descriptions: IDrivingStyleMetricDescriptionDetails[] = [];

        denominatorKeys.forEach(key => {
            const metricMapping: DrivingStyleMetric = DrivingStyleMetric[key];
            const denominator: DrivingStyleMetricDenominator = metricDenominatorMap[key];

            descriptions.push({
                DrivingStyleMetric: metricMapping,
                Description: this.createMetricDescription(metricMapping, denominator),
                Denominator: denominator
            });
        });

        return descriptions;
    }

    calculateDrivingScore(drivingStyle: IDrivingStyle, invalidateCache: boolean = false): Promise<IDrivingPerformanceSummary> {

        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        const styleWithJourneys: IJourneysWithAssociatedDrivingStyle = {
            DrivingStyle: drivingStyle
        };

        return lastValueFrom(this.httpClient.post<IDrivingPerformanceSummary>(`/api/drivingstyles/calculateDrivingScoreForJourneys`, styleWithJourneys, options)).then(response => {
            this.translateCalculationDescriptions(response);

            return response;
        });
    }

    getDrivingStyleMetricsWithDenominations(invalidateCache: boolean = false): Promise<DrivingStyleMetricDenominatorMap> {

        const options = { params: {} as HttpParams }

        options.params["minsToLive"] = 30;
        options.params["invalidateCache"] = invalidateCache;

        return lastValueFrom(this.httpClient.get<DrivingStyleMetricDenominatorMap>(`/api/drivingstyles/styleMetricWithDenominations`, options)).then(response => {
            return response;
        });
    }

    getMetricMappingWeightingLevelDetails(): IMetricMappingWeightingLevelDetail[] {
        const metricMappingWeightingLevelDetails: IMetricMappingWeightingLevelDetail[] = [
            { level: MetricMappingWeightingLevel.Low, value: 10, icon: "expand_more" },
            { level: MetricMappingWeightingLevel.Medium, value: 25, icon: "horizontal_rule", isDefault: true },
            { level: MetricMappingWeightingLevel.High, value: 45, icon: "expand_less" },
            { level: MetricMappingWeightingLevel.Critical, value: 60, icon: "keyboard_double_arrow_up" },
            { level: MetricMappingWeightingLevel.Custom, value: 1, icon: "", hideFromDropdown: true }
        ];

        return metricMappingWeightingLevelDetails;
    }

    createDefaultDrivingStyle(): IDrivingStyle {
        const newDrivingStyle: IDrivingStyle = {
            Id: 0,
            Name: "",
            DrivingStyleAssociations: [],
            DrivingStyleMetricMappings: [],
            UpperRagBound: 0,
            LowerRagBound: 0,
            WeightingType: DrivingStyleWeightingType.Even,
            DrivingStyleType: DrivingStyleType.Efficiency
        };

        return newDrivingStyle;
    }

    createDefaultDrivingStyleAssociation(): IDrivingStyleAssociation {
        const newDrivingStyleAssociation: IDrivingStyleAssociation = {
            Id: 0,
            DrivingStyleId: 0
        }

        return newDrivingStyleAssociation;
    }

    createDrivingStyleMetricMapping(drivingStyleMetric: DrivingStyleMetric, drivingStyleId: number = -1): IDrivingStyleMetricMapping {
        const newDrivingStyleMetricMapping: IDrivingStyleMetricMapping = {
            Id: 0,
            DrivingStyleId: drivingStyleId,
            DrivingStyleMetric: drivingStyleMetric,
            LowerRagBound: 40,
            UpperRagBound: 70,
            HighScoreValue: 0,
            LowScoreValue: 10,
            Created: "",
            Updated: "",
            Type: this.setDrivingMetricType(drivingStyleMetric)
        }

        // temporary until default style includes this new metric
        if (drivingStyleMetric === DrivingStyleMetric.CruiseDuration) {
            newDrivingStyleMetricMapping.HighScoreValue = 10;
            newDrivingStyleMetricMapping.LowScoreValue = 0;
        }

        return newDrivingStyleMetricMapping;
    }

    addWeightingLevelDetailsToMetricMappings(metricMappings: IDrivingStyleMetricMapping[] = []): void {
        const weightingLevelDetails: IMetricMappingWeightingLevelDetail[] = this.getMetricMappingWeightingLevelDetails();

        if (!metricMappings?.length || !weightingLevelDetails?.length) {
            return;
        }

        metricMappings.forEach(metricMapping => this.updateMetricWithWeightingLevelDetails(metricMapping, weightingLevelDetails));
    }

    addDrivingStyleMetricDefaults(metricMappings: IDrivingStyleMetricMapping[] = [], defaultDrivingStyle: IDrivingStyle): void {
        if (!metricMappings?.length || !defaultDrivingStyle?.DrivingStyleMetricMappings?.length) {
            return;
        }

        metricMappings.forEach(metricMapping => this.addDrivingStyleMetricDefault(metricMapping, defaultDrivingStyle));
    }

    addDrivingStyleMetricDefault(metricMapping: IDrivingStyleMetricMapping, defaultDrivingStyle: IDrivingStyle): void {
        const matchingDefaultMetric = defaultDrivingStyle.DrivingStyleMetricMappings.find(defaultMapping => defaultMapping.DrivingStyleMetric === metricMapping.DrivingStyleMetric);

        if (!matchingDefaultMetric) {
            console.warn(`Couldn't find metric defaults for `, metricMapping);
            return;
        }

        metricMapping.defaults = copy(matchingDefaultMetric);
    }

    addDrivingStyleMetricDescriptionDetails(metricMappings: IDrivingStyleMetricMapping[] = [], metricDescriptions: IDrivingStyleMetricDescriptionDetails[]): void {
        if (!metricMappings?.length || !metricDescriptions) {
            return;
        }

        metricMappings.forEach(metricMapping => this.addDrivingStyleMetricDescription(metricMapping, metricDescriptions));
    }

    addDrivingStyleMetricDescription(metricMapping: IDrivingStyleMetricMapping, metricDescriptionDetails: IDrivingStyleMetricDescriptionDetails[]): IDrivingStyleMetricMapping {
        metricMapping.isTotal = false;

        const descriptionDetail = metricDescriptionDetails.find(descriptionDetail => descriptionDetail.DrivingStyleMetric === metricMapping.DrivingStyleMetric);

        metricMapping.drivingStyleMetricDescription = descriptionDetail.Description;
        metricMapping.drivingStyleMetricName = this.translationService.tEnum(DrivingStyleMetric, "DrivingStyleMetric", metricMapping.DrivingStyleMetric);

        if (metricMapping.DrivingStyleMetric === DrivingStyleMetric.Total) {
            metricMapping.isTotal = true;
            metricMapping.drivingStyleMetricName = this.translationService.t("DrivingStylesService.OverallDrivingStyleMetricName");
            metricMapping.drivingStyleMetricDescription = this.translationService.t("DrivingStylesService.OverallDrivingStyleMetricDescription");

            return metricMapping;
        }

        metricMapping.unit = this.getUnitForMetricDenominatorLabel(descriptionDetail.Denominator);        
        
        metricMapping.category = this.setDrivingMetricCategory(metricMapping.DrivingStyleMetric);

        metricMapping.metricStyleType = this.setDrivingMetricType(metricMapping.DrivingStyleMetric);

        return metricMapping;
    }

    uniqueDrivingStyleNameValidator(drivingStyle: IDrivingStyle, drivingStyles: IDrivingStyle[]): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            if (!drivingStyles?.length || !control.value) {
                return null;
            }

            const isDuplicate = this.drivingStyleNameInUse(drivingStyle, drivingStyles);

            return isDuplicate ? { customMessage: this.translationService.t("DrivingStyleEditor.UniqueStyleNameWarning") } : null;
        };
    }

    drivingStyleNameInUse(drivingStyle: IDrivingStyle, drivingStyles: IDrivingStyle[]): boolean {
        if (!drivingStyle?.Name || !drivingStyles?.length) {
            return false;
        }

        const matchingStyles = drivingStyles.filter(existingDrivingStyle => {

            return existingDrivingStyle.Name.toLowerCase() === drivingStyle.Name.toLowerCase()
                && existingDrivingStyle.Id !== drivingStyle.Id
                && existingDrivingStyle.DrivingStyleType === drivingStyle.DrivingStyleType;
        });

        return matchingStyles?.length > 0;
    }

    setWeightingLevelDetailOnMetric(weightingLevelDetail: IMetricMappingWeightingLevelDetail, metricMapping: IDrivingStyleMetricMapping): void {
        metricMapping.weightingLevelDetails = weightingLevelDetail;
    }

    addWeightingLevelDetailsToMetric(metricMapping: IDrivingStyleMetricMapping, weightingType: DrivingStyleWeightingType): void {
        const metricMappingWeightlingLevelDetails = this.getMetricMappingWeightingLevelDetails();
        const matchingWeightingLevelDetail = metricMappingWeightlingLevelDetails.find(detail => detail.value === metricMapping.Weighting);

        if (weightingType === DrivingStyleWeightingType.Custom) {
            metricMapping.weightingLevelDetails = metricMappingWeightlingLevelDetails.find(detail => detail.level === MetricMappingWeightingLevel.Custom);
            return;
        }

        metricMapping.weightingLevelDetails = matchingWeightingLevelDetail;
    }

    createAssociationPillsForDrivingStyle(drivingStyle: IDrivingStyle, groups: IGroup[]): void {

        if (!drivingStyle) {
            return;
        }

        drivingStyle.displayPillsForAssociations = [];

        if (!drivingStyle.DrivingStyleAssociations?.length) {
            return;
        }

        drivingStyle.DrivingStyleAssociations.forEach(association => {
            if (this.isAllResourcesAssociation(association)) {

                drivingStyle.displayPillsForAssociations.push({
                    label: this.translationService.t("DrivingStylesManager.AllResourcesAssociation"),
                    icon: "checklist_rtl",
                    data: association
                });

                return;
            }

            const groupPill = this.getGroupPillFromAssociation(association, groups);
            const vehicleTypeLabel = this.getVehicleTypePillFromAssociation(association);

            if (groupPill && vehicleTypeLabel) {
                drivingStyle.displayPillsForAssociations.push(this.combinePills(groupPill, vehicleTypeLabel));
                return;
            }

            if (groupPill) {
                drivingStyle.displayPillsForAssociations.push(groupPill);
            }

            if (vehicleTypeLabel) {
                drivingStyle.displayPillsForAssociations.push(vehicleTypeLabel);
            }
        });

        drivingStyle.displayPillsForAssociations = this.stripDuplicateAssociations(drivingStyle.displayPillsForAssociations);
    }

    getDrivingStyleWeightingTypes(): IKeyValuePair<DrivingStyleWeightingType>[] {
        const weightingTypes = EnumService.getItemsForSelect<DrivingStyleWeightingType>(DrivingStyleWeightingType);

        weightingTypes.forEach(type => {
            type.displayName = this.translationService.translateEnum(DrivingStyleWeightingType, "DrivingStyleWeightingType", type.value as any);
        });

        weightingTypes.sort((weightingType1, weightingType2) => weightingType1.value - weightingType2.value);

        return weightingTypes;
    }

    updateCustomWeightingsError(drivingStyle: IDrivingStyle): void {

        drivingStyle.metricMappingsError = null;

        let totalWeighting = this.getTotalWeightingOnStyle(drivingStyle);

        if (drivingStyle) {
            drivingStyle.totalWeighting = totalWeighting;
        }

        if (drivingStyle.WeightingType !== DrivingStyleWeightingType.Custom) {
            drivingStyle.metricMappingsError = null;
            return;
        }

        const metricWithWeightingProblem = drivingStyle.DrivingStyleMetricMappings.find(mapping => mapping.Weighting === 0 || mapping.Weighting > 100);
        const hasIndividualWeightingError: boolean = !!metricWithWeightingProblem;

        if (hasIndividualWeightingError) {
            const metricTranslation: string = this.translationService.tEnum(DrivingStyleMetric, "DrivingStyleMetric", metricWithWeightingProblem.DrivingStyleMetric);
            drivingStyle.metricMappingsError = this.translationService.t("DrivingStylesService.CustomWeightingIssue", metricTranslation);
            return;
        }

        if (totalWeighting !== 100) {
            drivingStyle.metricMappingsError = this.translationService.t("DrivingStylesService.AllCustomWeightingsMustEqual100", totalWeighting);
        }
    }

    getTotalWeightingOnStyle(drivingStyle: IDrivingStyle): number {
        let totalWeighting = 0;

        if (!drivingStyle?.DrivingStyleMetricMappings?.length) {
            return totalWeighting;
        }

        drivingStyle.DrivingStyleMetricMappings.forEach(mapping => {
            totalWeighting += mapping.Weighting;
        });

        return totalWeighting;
    }

    // ---

    private createMetricDescription(metric: DrivingStyleMetric, denominator: DrivingStyleMetricDenominator): string {
        let metricTranslation = this.translationService.tEnum(DrivingStyleMetric, "DrivingStyleMetricEnum", metric);
        let denominatorTranslation = this.translationService.tEnum(DrivingStyleMetricDenominator, "DrivingStyleMetricDenominator", denominator);

        return `${metricTranslation} ${denominatorTranslation}`;
    }

    private getUnitForMetricDenominatorLabel(denominator: DrivingStyleMetricDenominator): string {
        switch (denominator) {
            case DrivingStyleMetricDenominator.PerHundredKm: return this.translationService.t("DrivingStylesService.Events");
            case DrivingStyleMetricDenominator.DurationEngineOn: return "%";
            case DrivingStyleMetricDenominator.DurationDriving: return "%";
            default: return "";
        }
    }

    private translateCalculationDescriptions(performanceSummary: IDrivingPerformanceSummary): void {
        if (!performanceSummary?.CalculationResult?.DrivingStyleMetricScoreMetadataItems?.length) {
            console.warn("No calculation result metadata items provided for driving style");
            return;
        }

        performanceSummary.CalculationResult.DrivingStyleMetricScoreMetadataItems.forEach(metadataItem => {
            const score: number = +(metadataItem.ProportionOfTotalScore ?? 0).toFixed(2);
            const decimalPipe = new DecimalPipe("en");

            metadataItem.calculationDescription = this.translationService.t("DrivingStylesService.WeightingOfTotalScoreLabel", decimalPipe.transform(score, "1.0-1"));
        });
    }

    private getGroupPillFromAssociation(association: IDrivingStyleAssociation, groups: IGroup[]): IPill<IDrivingStyleAssociation> {
        if (!association.GroupId && association.GroupId !== 0) {
            return;
        }

        if (!groups?.length) {
            console.warn("No groups to produce group names from");
            return;
        }

        const matchingGroup = groups.find(group => group.Id === association.GroupId);

        if (!matchingGroup) {
            console.warn("No matching group to produce group names from");
            return;
        }

        return {
            label: matchingGroup.Name,
            icon: matchingGroup.IsDepot ? "home" : "folder_open",
            data: association
        };
    }

    private getVehicleTypePillFromAssociation(association: IDrivingStyleAssociation): IPill<IDrivingStyleAssociation> {
        if (!VehicleType[association.VehicleType] || association.VehicleType === -1) {
            return;
        }

        return {
            label: this.translationService.tEnum(VehicleType, "VehicleType", association.VehicleType),
            icon: this.getIconForVehicleType(association.VehicleType),
            data: association
        };
    }

    private getIconForVehicleType(vehicleType: VehicleType): string {
        switch (vehicleType) {
            case VehicleType.Trailer: return "rv_hookup";
            case VehicleType.Car: return "directions_car";
            case VehicleType.BusOrPSV: return "airport_shuttle";
            case VehicleType.HgvLessThan12T:
            case VehicleType.Hgv12Tto22T:
            case VehicleType.HgvGreaterThan22T:
            case VehicleType.Lcv: return "local_shipping";
            case VehicleType.Motorcycle: return "two_wheeler";
            default: return;
        }
    }

    private stripDuplicateAssociations(tableCellPills: IPill<IDrivingStyleAssociation>[]): IPill<IDrivingStyleAssociation>[] {
        return tableCellPills.filter((pill, index, self) => self.findIndex((target) => target.label === pill.label) === index);
    }

    private isAllResourcesAssociation(association: IDrivingStyleAssociation): boolean {
        return !VehicleType[association.VehicleType] && !association.GroupId && association.GroupId !== 0;
    }

    private combinePills(pill1: IPill<IDrivingStyleAssociation>, pill2: IPill<IDrivingStyleAssociation>): IPill<IDrivingStyleAssociation> {
        return {
            label: `${pill1.label} & ${pill2.label}`,
            icon: "link",
            data: pill1.data
        }
    }

    private updateMetricWithWeightingLevelDetails(metricMapping: IDrivingStyleMetricMapping, metricMappingWeightingLevelDetails: IMetricMappingWeightingLevelDetail[]): void {
        if (!metricMapping.Weighting || metricMapping.Weighting < 1) {
            return;
        }

        const matchingWeightingLevelDetails = metricMappingWeightingLevelDetails.find(detail => detail.value === metricMapping.Weighting);

        if (matchingWeightingLevelDetails) {
            metricMapping.weightingLevelDetails = matchingWeightingLevelDetails;
            return;
        }

        metricMapping.weightingLevelDetails = metricMappingWeightingLevelDetails.find(detail => detail.level === MetricMappingWeightingLevel.Custom);
    }

    private setDrivingMetricType(metric: DrivingStyleMetric): DrivingStyleType {
        if ([
            DrivingStyleMetric.SmokingEventsOverDistance,
            DrivingStyleMetric.FatigueEventsOverDistance,
            DrivingStyleMetric.NoSeatBeltEventsOverDistance,
            DrivingStyleMetric.DriverOnPhoneEventsOverDistance,
            DrivingStyleMetric.DriverDistractionEventsOverDistance,
            DrivingStyleMetric.CollisionWarningAndVehicleTooCloseEventsOverDistance
        ].includes(metric)) {
            return DrivingStyleType.Safety;
        }

        if ([
            DrivingStyleMetric.HarshAccelerationEvents,
            DrivingStyleMetric.HarshBrakingEvents,
            DrivingStyleMetric.HarshCorneringEvents,
            DrivingStyleMetric.ContextualSpeedingEvents
        ].includes(metric)) {
            return DrivingStyleType.All;
        }

        if ([
            DrivingStyleMetric.OverIdlingDuration,
            DrivingStyleMetric.OverTorqueDuration,
            DrivingStyleMetric.OverAccelerationDuration,
            DrivingStyleMetric.CoastingInclusiveDuration,
            DrivingStyleMetric.OverRevDuration,
            DrivingStyleMetric.BadAnticipationEvents,
            DrivingStyleMetric.CruiseDuration,
            DrivingStyleMetric.CruiseInTopGearDuration,
            DrivingStyleMetric.GreenBandDuration,
            DrivingStyleMetric.SweetSpotDuration,
            DrivingStyleMetric.CoastingExclusiveDuration
        ].includes(metric)) {
            return DrivingStyleType.Efficiency;
        }

        return null;
    }

    private setDrivingMetricCategory(metric: DrivingStyleMetric): DrivingStyleMetricCategory {
        if ([
            DrivingStyleMetric.SmokingEventsOverDistance,
            DrivingStyleMetric.FatigueEventsOverDistance,
            DrivingStyleMetric.NoSeatBeltEventsOverDistance,
            DrivingStyleMetric.DriverOnPhoneEventsOverDistance,
            DrivingStyleMetric.DriverDistractionEventsOverDistance,
            DrivingStyleMetric.CollisionWarningAndVehicleTooCloseEventsOverDistance
        ].includes(metric)) {
            return DrivingStyleMetricCategory.ADASDMS;
        }

        if([
            DrivingStyleMetric.HarshBrakingEvents,
            DrivingStyleMetric.HarshAccelerationEvents,
            DrivingStyleMetric.HarshCorneringEvents
        ].includes(metric)) {
            return DrivingStyleMetricCategory.Accelerometer
        }

        if([
            DrivingStyleMetric.ContextualSpeedingEvents        
        ].includes(metric)) {
            return DrivingStyleMetricCategory.Tracking
        }

         return DrivingStyleMetricCategory.CAN;
    }

    
}
